/**
 * useMediaQuery — React hook for subscribing to a CSS media query.
 *
 * Returns `true` whenever the supplied media-query string matches the
 * current viewport, recomputing live as the viewport resizes or as the
 * `prefers-color-scheme`-style system signal flips. SSR-safe (returns
 * `false` until first browser paint).
 *
 * Spec: meridian-redesign — task (k)
 * Requirements: 14.8 — responsive design across all surfaces
 *
 * Tailwind breakpoint helpers exported as named constants below mirror
 * the framework's default `theme.screens` values so callers don't need
 * to repeat the magic numbers:
 *
 *   `useIsBelowMd()` → true at viewports `< 768px`
 *     — the redesigned portal/admin sidebars auto-collapse here
 *       (PR (k)), and `MessagesPage` swaps to the native-app handoff
 *       (PR (l)).
 *
 *   `useIsBelowSm()` → true at viewports `< 640px`
 *     — kept available for future single-column-only adaptations.
 */

import { useEffect, useState } from 'react';

/**
 * Subscribe to a CSS media query. Re-renders whenever the query's
 * match state changes.
 *
 * Returns `false` during SSR (when `window` is undefined) and during
 * the first render before the effect attaches a listener — callers
 * that flicker visibly on hydration should hide the affected element
 * with `useState(false)` until `mounted === true` if a stable initial
 * paint matters more than catching the very first frame.
 *
 * @param query A standard CSS media query string, e.g.
 *   `'(max-width: 767px)'` or `'(prefers-color-scheme: dark)'`.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const list = window.matchMedia(query);
    const onChange = (event: MediaQueryListEvent) => setMatches(event.matches);
    // Set immediately in case the SSR-safe initializer returned a stale
    // value (e.g. test environment that mocks matchMedia).
    setMatches(list.matches);
    // Modern Safari and every other current browser support
    // addEventListener; older Safari (<14) needs addListener. Try the
    // modern API first, fall back to the legacy one if absent.
    if (typeof list.addEventListener === 'function') {
      list.addEventListener('change', onChange);
      return () => list.removeEventListener('change', onChange);
    }
    // Legacy Safari fallback.
    list.addListener(onChange);
    return () => list.removeListener(onChange);
  }, [query]);

  return matches;
}

/**
 * True when the viewport is **strictly narrower** than Tailwind's
 * default `md` breakpoint (768px). Use to drive layout collapse
 * behaviors (e.g. sidebar shrinks to icon-only) on phone-sized
 * screens.
 */
export function useIsBelowMd(): boolean {
  return useMediaQuery('(max-width: 767.98px)');
}

/**
 * True when the viewport is **strictly narrower** than Tailwind's
 * default `sm` breakpoint (640px).
 */
export function useIsBelowSm(): boolean {
  return useMediaQuery('(max-width: 639.98px)');
}
